import { Dispatch, SetStateAction, useCallback, useMemo, useRef, useState } from "react";
import { Map as MapGL, MapRef } from "react-map-gl/mapbox";

import { accountOwnedMapStyleUrl, knownMapboxStyles } from "@/lib/mapbox/base-layer-styles";
import useMapViewState from "@/features/shared/widgets/map/hooks/useMapViewState";
import { EarlyWarningTableWidgetDataWarningsInner } from "@/lib/api";
import { DeckGLOverlay } from "@/components/deckgl/deckgl-overlay";

import Styled from "./styles";
import { EarlyWarningTableToggles } from "./early-warning-table";
import {
  getGeoJsonLayerFromWarning,
  getIconLayerFromWarning,
  sortWarningsByLevel,
} from "./utils";

interface EarlyWarningMapThumbProps {
  index: number;
  warnings: EarlyWarningTableWidgetDataWarningsInner[];
  setToggle: Dispatch<SetStateAction<EarlyWarningTableToggles>>;
  setSelectedDay: Dispatch<SetStateAction<number>>;
}

const EarlyWarningMapThumb = ({
  index,
  warnings,
  setToggle,
  setSelectedDay,
}: EarlyWarningMapThumbProps) => {
  const mapTileAccessToken = import.meta.env.VITE_MAP_ACCESS_TOKEN;

  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapViewStateOptions = useMemo(
    () => ({
      containerRef: mapContainerRef,
      initialCoords: [
        { lat: 53.57885, lng: 3.2853652 },
        { lat: 51, lng: 7.279572 },
      ],
    }),
    [mapContainerRef.current],
  );
  const { mapViewState } = useMapViewState(mapViewStateOptions);

  const handleClick = useCallback(() => {
    setToggle(EarlyWarningTableToggles.MAP);
    setSelectedDay(index);
  }, [setSelectedDay, setToggle, index]);

  const deckRef = useRef<MapRef>(null);
  const [canvasDataUrl, setCanvasDataUrl] = useState<string | undefined>(undefined);
  const handleAfterRender = useCallback(() => {
    const dataUrl = deckRef.current?.getCanvas()?.toDataURL();
    setCanvasDataUrl(dataUrl);
  }, [deckRef, setCanvasDataUrl]);

  const layers = useMemo(() => {
    const sortedWarnings = sortWarningsByLevel(warnings);
    const geojsonLayers = sortedWarnings.map((warning) =>
      getGeoJsonLayerFromWarning(warning),
    );
    const iconLayer = getIconLayerFromWarning(sortedWarnings, undefined);

    return [...geojsonLayers, iconLayer];
  }, [warnings]);

  return (
    <Styled.MapThumb
      ref={mapContainerRef}
      style={{ backgroundImage: `url("${canvasDataUrl}")` }}
    >
      {mapViewState && (
        <MapGL
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error DeckGl MapViewState does not match perfectly
          viewState={mapViewState}
          attributionControl={false}
          projection={{
            name: "mercator",
          }}
          mapStyle={accountOwnedMapStyleUrl(knownMapboxStyles.MonochromeLightWithoutLabels)}
          mapboxAccessToken={mapTileAccessToken}
          maxPitch={0}
          bearingSnap={360}
          bearing={0}
          interactive={false}
          onClick={handleClick}
          onRender={handleAfterRender}
          ref={deckRef}
        >
          <DeckGLOverlay interleaved controller layers={layers} />
        </MapGL>
      )}
    </Styled.MapThumb>
  );
};

export default EarlyWarningMapThumb;
